class InteractiveFeatures {
    constructor(sectionId) {
        this.sectionId = sectionId;
        this.section = document.querySelector(`[data-section-id="${sectionId}"]`);
        this.desktopWrapper = this.section?.querySelector(`#int-desktop-${sectionId}`);
        this.mobileWrapper = this.section?.querySelector(`#int-mobile-${sectionId}`);
        this.mobileControls = this.section?.querySelector(`#mobile-controls-${sectionId}`);
        this.mobileScroll = this.section?.querySelector(`#mobile-scroll-${sectionId}`);
        
        this.init();
    }
    
    init() {
        if (this.desktopWrapper) this.initDesktopLayout();
        if (this.mobileWrapper) this.initMobileLayout();
    }
    
    initDesktopLayout() {
        const detailsElements = this.desktopWrapper.querySelectorAll('.int-details');
        const imgBlocks = this.desktopWrapper.querySelectorAll('.int-img-block');
        const prevButton = this.desktopWrapper.querySelector('[data-action="previous"]');
        const nextButton = this.desktopWrapper.querySelector('[data-action="next"]');
        const exitButton = this.desktopWrapper.querySelector('[data-action="exit"]');
        const backgroundImg = this.desktopWrapper.querySelector('.int-background');

        const getOpenDetails = () => {
            return Array.from(detailsElements).findIndex(details => details.classList.contains('open'));
        };

        const updateImages = (openIndex) => {
            imgBlocks.forEach((imgBlock, index) => {
                if (index === openIndex) {
                    imgBlock.classList.add('active');
                } else {
                    imgBlock.classList.remove('active');
                }
            });
            
            if (openIndex !== -1 && backgroundImg) {
                backgroundImg.style.opacity = '0';
            } else if (backgroundImg) {
                backgroundImg.style.opacity = '1';
            }
        };

        const openDetails = (index) => {
            detailsElements.forEach((details, i) => {
                if (i === index) {
                    details.classList.add('open');
                    details.querySelector('.int-summary').setAttribute('aria-expanded', 'true');
                    this.desktopWrapper.classList.add('has-open');
                } else {
                    details.classList.remove('open');
                    details.querySelector('.int-summary').setAttribute('aria-expanded', 'false');
                }
            });
            updateImages(index);
        };

        const closeAllDetails = () => {
            detailsElements.forEach(details => {
                details.classList.remove('open');
                details.querySelector('.int-summary').setAttribute('aria-expanded', 'false');
            });
            this.desktopWrapper.classList.remove('has-open');
            updateImages(-1);
        };

        detailsElements.forEach((details, index) => {
            const summary = details.querySelector('.int-summary');
            
            summary.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                
                if (details.classList.contains('open')) {
                    closeAllDetails();
                } else {
                    openDetails(index);
                }
            });
        });

        prevButton?.addEventListener('click', (e) => {
            e.stopPropagation();
            const currentIndex = getOpenDetails();
            if (currentIndex !== -1) {
                const prevIndex = (currentIndex - 1 + detailsElements.length) % detailsElements.length;
                openDetails(prevIndex);
            } else {
                openDetails(detailsElements.length - 1);
            }
        });

        nextButton?.addEventListener('click', (e) => {
            e.stopPropagation();
            const currentIndex = getOpenDetails();
            if (currentIndex !== -1) {
                const nextIndex = (currentIndex + 1) % detailsElements.length;
                openDetails(nextIndex);
            } else {
                openDetails(0);
            }
        });

        exitButton?.addEventListener('click', (e) => {
            e.stopPropagation();
            closeAllDetails();
        });

        document.addEventListener('keydown', (e) => {
            if (!this.desktopWrapper.classList.contains('has-open')) return;
            
            if (e.key === 'Escape') {
                closeAllDetails();
            } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                const currentIndex = getOpenDetails();
                if (currentIndex !== -1) {
                    const prevIndex = (currentIndex - 1 + detailsElements.length) % detailsElements.length;
                    openDetails(prevIndex);
                }
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                const currentIndex = getOpenDetails();
                if (currentIndex !== -1) {
                    const nextIndex = (currentIndex + 1) % detailsElements.length;
                    openDetails(nextIndex);
                }
            }
        });

        this.desktopWrapper.addEventListener('click', (e) => {
            if (e.target === this.desktopWrapper) {
                closeAllDetails();
            }
        });
        
        updateImages(-1);
    }
    
    initMobileLayout() {
        const detailsElements = this.mobileWrapper.querySelectorAll('.int-details');
        const imgBlocks = this.mobileWrapper.querySelectorAll('.int-img-block');
        const backgroundImg = this.mobileWrapper.querySelector('.int-background');
        const expandedContent = this.mobileWrapper.querySelector('.int-expanded-content');
        const expandedText = this.mobileWrapper.querySelector('.int-expanded-text');
        const expandedPrev = this.mobileWrapper.querySelector('[data-action="mobile-expanded-prev"]');
        const expandedNext = this.mobileWrapper.querySelector('[data-action="mobile-expanded-next"]');
        const expandedClose = this.mobileWrapper.querySelector('[data-action="mobile-close"]');
        
        let currentOpenIndex = -1;
        let isScrolling = false;
        let scrollTimer;
        let touchStartX = 0;
        let touchStartY = 0;
        let touchEndX = 0;
        let touchEndY = 0;
        let isSwiping = false;
        const swipeThreshold = 30;

        let isDragging = false;
        let startX = 0;
        let scrollLeft = 0;

        const enableTouchScrolling = () => {
            this.mobileScroll.addEventListener('touchstart', (e) => {
                isDragging = true;
                startX = e.touches[0].pageX - this.mobileScroll.offsetLeft;
                scrollLeft = this.mobileScroll.scrollLeft;
                this.mobileScroll.style.scrollSnapType = 'none';
            }, { passive: true });

            this.mobileScroll.addEventListener('touchmove', (e) => {
                if (!isDragging) return;
                e.preventDefault();
                const x = e.touches[0].pageX - this.mobileScroll.offsetLeft;
                const walk = (x - startX) * 2;
                this.mobileScroll.scrollLeft = scrollLeft - walk;
            }, { passive: false });

            this.mobileScroll.addEventListener('touchend', () => {
                isDragging = false;
                setTimeout(() => {
                    this.mobileScroll.style.scrollSnapType = 'x mandatory';
                }, 100);
            }, { passive: true });
        };

        enableTouchScrolling();

        const getOpenDetails = () => {
            return currentOpenIndex;
        };

        const updateImages = (openIndex) => {
            imgBlocks.forEach((imgBlock, index) => {
                if (index === openIndex) {
                    imgBlock.classList.add('active');
                } else {
                    imgBlock.classList.remove('active');
                }
            });
            
            if (openIndex !== -1 && backgroundImg) {
                backgroundImg.style.opacity = '0';
            } else if (backgroundImg) {
                backgroundImg.style.opacity = '1';
            }
        };

        const openExpandedView = (index) => {
            if (index < 0 || index >= detailsElements.length) return;
            
            const detail = detailsElements[index];
            const content = detail.querySelector('.int-content').innerHTML;
            
            expandedText.innerHTML = content;
            this.mobileControls.classList.add('expanded');
            currentOpenIndex = index;
            updateImages(index);
        };

        const closeExpandedView = () => {
            this.mobileControls.classList.remove('expanded');
            currentOpenIndex = -1;
            updateImages(-1);
        };

        const navigateExpanded = (direction) => {
            if (currentOpenIndex === -1) return;
            
            let newIndex;
            if (direction === 'next') {
                newIndex = (currentOpenIndex + 1) % detailsElements.length;
            } else {
                newIndex = (currentOpenIndex - 1 + detailsElements.length) % detailsElements.length;
            }
            
            openExpandedView(newIndex);
        };

        detailsElements.forEach((details, index) => {
            const summary = details.querySelector('.int-summary');
            let touchMoved = false;
            
            summary.addEventListener('touchstart', (e) => {
                touchStartX = e.touches[0].clientX;
                touchStartY = e.touches[0].clientY;
                touchMoved = false;
                isSwiping = false;
            }, { passive: true });
            
            summary.addEventListener('touchmove', (e) => {
                if (!touchMoved) {
                    const currentX = e.touches[0].clientX;
                    const currentY = e.touches[0].clientY;
                    const diffX = Math.abs(currentX - touchStartX);
                    const diffY = Math.abs(currentY - touchStartY);
                    
                    if (diffX > swipeThreshold || diffY > swipeThreshold) {
                        touchMoved = true;
                        isSwiping = true;
                    }
                }
            }, { passive: true });
            
            summary.addEventListener('touchend', (e) => {
                touchEndX = e.changedTouches[0].clientX;
                touchEndY = e.changedTouches[0].clientY;
                
                if (!touchMoved && !isSwiping) {
                    e.preventDefault();
                    openExpandedView(index);
                }
                
                touchMoved = false;
                isSwiping = false;
            }, { passive: false });
            
            summary.addEventListener('click', (e) => {
                if (!touchMoved && !isSwiping) {
                    e.preventDefault();
                    openExpandedView(index);
                }
            });
        });

        this.mobileScroll.addEventListener('scroll', () => {
            isScrolling = true;
            clearTimeout(scrollTimer);
            scrollTimer = setTimeout(() => {
                isScrolling = false;
            }, 100);
        });

        expandedPrev?.addEventListener('click', (e) => {
            e.stopPropagation();
            navigateExpanded('prev');
        });

        expandedNext?.addEventListener('click', (e) => {
            e.stopPropagation();
            navigateExpanded('next');
        });

        expandedClose?.addEventListener('click', (e) => {
            e.stopPropagation();
            closeExpandedView();
        });

        expandedContent.addEventListener('touchstart', (e) => {
            touchStartX = e.touches[0].clientX;
            touchStartY = e.touches[0].clientY;
        }, { passive: true });

        expandedContent.addEventListener('touchmove', (e) => {
            if (currentOpenIndex === -1) return;
            
            const currentX = e.touches[0].clientX;
            const diffX = currentX - touchStartX;
            
            if (Math.abs(diffX) > swipeThreshold) {
                isSwiping = true;
            }
        }, { passive: true });

        expandedContent.addEventListener('touchend', (e) => {
            if (currentOpenIndex === -1 || !isSwiping) return;
            
            touchEndX = e.changedTouches[0].clientX;
            const diffX = touchEndX - touchStartX;
            
            if (Math.abs(diffX) > swipeThreshold) {
                if (diffX > 0) {
                    navigateExpanded('prev');
                } else {
                    navigateExpanded('next');
                }
            }
            
            isSwiping = false;
        }, { passive: true });

        this.mobileWrapper.addEventListener('click', (e) => {
            if (currentOpenIndex !== -1 && e.target === this.mobileWrapper) {
                closeExpandedView();
            }
        });

        document.addEventListener('keydown', (e) => {
            if (currentOpenIndex === -1) return;
            
            if (e.key === 'Escape') {
                closeExpandedView();
            } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                navigateExpanded('prev');
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                navigateExpanded('next');
            }
        });
        
        updateImages(-1);
    }
}

// Initialize interactive features when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
    const interactiveContainers = document.querySelectorAll('.int-container');
    
    interactiveContainers.forEach(container => {
        const sectionId = container.dataset.sectionId;
        if (sectionId) {
            new InteractiveFeatures(sectionId);
        }
    });
});